// CameraCapture.js — Mobile camera capture for receipt photos
// Uses getUserMedia() to access the device camera, captures a photo,
// and returns it as a File object compatible with the existing upload flow.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Typography,
  Button,
} from '@mui/material';
import { Close, CameraAlt, CheckCircle, FlipCameraAndroid } from '@mui/icons-material';

/**
 * CameraCapture — opens device camera, captures a photo, returns File.
 *
 * @param {boolean} open - Dialog visibility
 * @param {function} onClose - Close handler
 * @param {function} onCapture - Called with (File) when photo is captured
 * @param {string} accept - MIME types (default: image/jpeg)
 */
export default function CameraCapture({ open, onClose, onCapture, accept = 'image/jpeg' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null); // dataURL of captured photo
  const [facingMode, setFacingMode] = useState('environment'); // back camera by default
  const [error, setError] = useState(null);

  // Start camera when dialog opens
  useEffect(() => {
    if (open) {
      setCaptured(null);
      setError(null);
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facingMode]);

  const startCamera = async () => {
    try {
      // Stop any existing stream first
      stopCamera();

      const constraints = {
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('[CameraCapture] Failed to start camera:', err);
      setError(
        err.name === 'NotAllowedError'
          ? 'Camera access denied. Please allow camera permission in your browser settings.'
          : err.name === 'NotFoundError'
            ? 'No camera found on this device.'
            : `Camera error: ${err.message}`
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    // Get data URL
    const dataURL = canvas.toDataURL('image/jpeg', 0.92);
    setCaptured(dataURL);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!captured) return;

    // Convert dataURL to File
    const arr = captured.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);

    const file = new File([u8arr], `receipt_${Date.now()}.jpg`, { type: mime });
    onCapture(file);
    onClose();
  }, [captured, onCapture, onClose]);

  const handleRetake = () => {
    setCaptured(null);
  };

  const handleFlipCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {captured ? 'Review Photo' : 'Take Receipt Photo'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative', bgcolor: '#000' }}>
        {error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" variant="body2">{error}</Typography>
            <Button onClick={startCamera} sx={{ mt: 2 }} variant="outlined" size="small">
              Try Again
            </Button>
          </Box>
        ) : captured ? (
          // Show captured photo
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={captured}
              alt="Captured receipt"
              style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
            />
          </Box>
        ) : (
          // Show live camera preview
          <Box sx={{ position: 'relative' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', maxHeight: '60vh', objectFit: 'cover', display: 'block' }}
            />
            {/* Flip camera button (only show if multiple cameras) */}
            <IconButton
              onClick={handleFlipCamera}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
              size="small"
            >
              <FlipCameraAndroid fontSize="small" />
            </IconButton>
          </Box>
        )}
        {/* Hidden canvas for capturing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', p: 2, gap: 1 }}>
        {captured ? (
          <>
            <Button onClick={handleRetake} variant="outlined" size="small">
              Retake
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              size="small"
              startIcon={<CheckCircle />}
            >
              Use Photo
            </Button>
          </>
        ) : (
          <IconButton
            onClick={capturePhoto}
            disabled={!!error}
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'white',
              border: '4px solid #ccc',
              '&:hover': { bgcolor: '#f0f0f0', border: '4px solid #999' },
              '&:active': { transform: 'scale(0.95)' },
            }}
          >
            <CameraAlt sx={{ color: error ? '#ccc' : '#333', fontSize: 28 }} />
          </IconButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Check if camera is available on this device
 */
export function isCameraAvailable() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}
